import { useState, useEffect } from 'react';

// The header's bottom-docked navigation bar. It auto-hides as the user scrolls
// down and slides back in on scroll up, independently of any sticky bar. This is
// the same node @thd-olt-functional/utils' useHeaderObserver watches. It is not
// rendered by the Sticky component, so it needs its own detection path below.
const FOOTER_NAV_SELECTOR = '#header-nav-container';

// Our own app mounts into this node. The Sticky consumers we render (the FAB and
// the mobile drawer) live inside it, so they match the consumer signature below.
// We must exclude them, or the lift would feed back on the very element it moves.
const OWN_ROOT_SELECTOR = '#magic-apron-loader';

// The Sticky component (@thd-olt-component-react/sticky) always renders a
// min-height wrapper around an inner node whose class set contains "sticky".
// Matching that rendered signature lets us discover every Sticky consumer on the
// page — there can be more than one, they may or may not be present, and we
// can't enumerate them all — instead of hard-coding the handful we know about.
const STICKY_CONSUMER_SELECTOR = '[style^="min-height"] > [class*="sticky"].sui-bottom-0';

// Bottom-docked bars that are plain position:fixed nodes rather than Sticky
// consumers, so the signature above can't find them. Each must be listed here.
// - Cart: the "Cart Total / Checkout" footer (MAG4-896).
const FIXED_BOTTOM_BAR_SELECTORS = [
  '[data-automation-id="checkoutNowButton"]',
];

const getViewportHeight = () => (typeof window !== 'undefined' ? window.innerHeight : 0);

// How many pixels a bottom-docked element pokes up into the viewport, given the
// top of its bounding rect. A hidden element reports a top of 0, and a slid-away
// one a top at/below the bottom edge; both must read as "not intruding" (0)
// rather than "intruding the full viewport height", which would invert the lift
// and push the button up when an element actually disappears.
const intrusionFromTop = (top, viewportHeight) => (
  Number.isFinite(top) && top > 0 && top < viewportHeight ? viewportHeight - top : 0
);

// Intrusion of an element from the bottom of the viewport, or 0 when it is
// hidden, detached, or collapsed (which all report an empty, all-zero rect).
const getElementIntrusion = (element, viewportHeight) => {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return 0;
  return intrusionFromTop(rect.top, viewportHeight);
};

// The highest a Sticky consumer pokes up from the bottom of the viewport, across
// every consumer on the page. Consumers stack and overlap independently, so we
// lift above whichever one reaches highest; absent, hidden, or self-owned
// consumers contribute 0. A consumer counts only while it is actually intruding
// from the bottom — sites hide bars via the sui-hidden class when the real
// content scrolls into view, and top-docked / slid-away ones report a top that
// intrusionFromTop already reads as "not intruding".
const getStickyConsumerIntrusion = (viewportHeight) => {
  const consumers = document.querySelectorAll(STICKY_CONSUMER_SELECTOR);
  let maxIntrusion = 0;
  consumers.forEach((element) => {
    // Skip the consumers we render ourselves and any that are hidden.
    if (element.closest(OWN_ROOT_SELECTOR)) return;
    if (element.classList.contains('sui-hidden')) return;
    maxIntrusion = Math.max(maxIntrusion, getElementIntrusion(element, viewportHeight));
  });
  return maxIntrusion;
};

const getFixedBarIntrusion = (viewportHeight) => FIXED_BOTTOM_BAR_SELECTORS.reduce(
  (maxIntrusion, selector) => Math.max(
    maxIntrusion,
    getElementIntrusion(document.querySelector(selector), viewportHeight),
  ),
  0,
);

// The bottom nav only intrudes while it is actually docked at the bottom
// (data-position-bottom); otherwise it lives in the normal header flow.
const getFooterIntrusion = (viewportHeight) => {
  const nav = document.querySelector(FOOTER_NAV_SELECTOR);
  if (!nav || !('positionBottom' in nav.dataset)) return 0;
  return getElementIntrusion(nav, viewportHeight);
};

export const useStickyElementOffset = () => {
  const [offsetMargin, setOffsetMargin] = useState(0);

  useEffect(() => {
    let frame = null;
    // Our own re-renders write styles inside #magic-apron-loader (under #root).
    // Left unfiltered, every lift we apply mutates the observed subtree and
    // schedules another measure — a read→setState→write→observe→read loop that
    // burns a frame and a forced reflow per settle. Real intruders live
    // elsewhere in #root, so ignore bursts that are only our own writes.
    const ownRoot = document.querySelector(OWN_ROOT_SELECTOR);

    const measure = () => {
      const viewportHeight = getViewportHeight();
      // The footer and every sticky bar move independently: a bar may sit on top
      // of the footer, obscure it, or hide entirely, and there may be several
      // bars at once. Lift the button to clear whichever one pokes up highest
      // from the bottom of the viewport.
      setOffsetMargin(Math.max(
        getFooterIntrusion(viewportHeight),
        getStickyConsumerIntrusion(viewportHeight),
        getFixedBarIntrusion(viewportHeight),
      ));
    };

    const schedule = (records) => {
      // MutationObserver passes a records array; the event listeners below pass
      // an Event. Only records can be "ours", so only they get filtered.
      if (Array.isArray(records) && ownRoot
        && records.every((record) => ownRoot.contains(record.target))) {
        return;
      }
      // Both intruders change in bursts (class toggles, scroll-driven slides).
      // Coalesce into a single measurement per animation frame so we don't
      // thrash layout by reading getBoundingClientRect() on every event.
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    // Sticky bars hydrate in and toggle their visibility client-side, after the
    // server-rendered shell mounts. Observing the whole app root (rather than the
    // specific fusion sections one known bar lives in) catches any consumer
    // appearing or toggling sui-hidden, wherever it renders.
    const observerRoot = document.querySelector('#root');
    const observer = new MutationObserver(schedule);
    if (observerRoot) {
      observer.observe(observerRoot, { attributes: true, childList: true, subtree: true });
    }

    // The footer slides in/out on scroll and shifts on resize. Those moves don't
    // mutate the DOM, so the MutationObserver can't see them — listen for the
    // events directly. transitionend settles the position once the slide
    // animation finishes after scrolling stops.
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('transitionend', schedule);

    measure();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('transitionend', schedule);
    };
  }, []);

  return offsetMargin;
};
